import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, googleProvider, db } from '../config/firebase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setError(null);
      console.log('Starting Google sign-in...');
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      console.log('Google sign-in successful:', user.email);

      // Check if user document exists in Firestore with retry
      const userRef = doc(db, 'users', user.uid);

      let retryCount = 0;
      const maxRetries = 3;
      let userSnap = null;

      while (retryCount < maxRetries) {
        try {
          console.log(`Fetching user document (attempt ${retryCount + 1})...`);
          userSnap = await getDoc(userRef);
          break; // Success, exit retry loop
        } catch (firestoreErr) {
          console.warn(`Firestore attempt ${retryCount + 1} failed:`, firestoreErr.message);
          retryCount++;
          if (retryCount >= maxRetries) {
            console.error('Max retries reached for Firestore');
            // Continue without Firestore - authentication still succeeded
            return user;
          }
          // Wait before retry (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
        }
      }

      if (userSnap && !userSnap.exists()) {
        // Create new user document
        const adminEmail = import.meta.env.VITE_ADMIN_EMAIL || 'buildmedia@gmail.com';
        const isAdmin = user.email === adminEmail;
        console.log('Creating new user document, isAdmin:', isAdmin);

        try {
          await setDoc(userRef, {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            role: isAdmin ? 'admin' : 'client',
            createdAt: serverTimestamp(),
            lastLoginAt: serverTimestamp(),
            notificationPreferences: {
              email: true,
              inApp: true,
              orderUpdates: true,
              projectUpdates: true,
            },
          });
          console.log('User document created successfully');
        } catch (writeErr) {
          console.warn('Failed to create user document:', writeErr.message);
          // Continue - authentication succeeded even if Firestore write failed
        }
      } else if (userSnap) {
        // Update last login time
        console.log('Updating existing user last login');
        try {
          await setDoc(
            userRef,
            { lastLoginAt: serverTimestamp() },
            { merge: true }
          );
        } catch (updateErr) {
          console.warn('Failed to update last login:', updateErr.message);
          // Non-critical, continue
        }
      }

      return user;
    } catch (err) {
      console.error('Error signing in with Google:', err);
      setError(err.message);
      throw err;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
    } catch (err) {
      console.error('Error signing out:', err);
      setError(err.message);
      throw err;
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Get user data from Firestore
        try {
          const userRef = doc(db, 'users', firebaseUser.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            setUser({
              ...firebaseUser,
              role: userData.role,
              customData: userData,
            });
          } else {
            setUser(firebaseUser);
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          setUser(firebaseUser);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = {
    user,
    loading,
    error,
    signInWithGoogle,
    signOut,
    isAdmin: user?.role === 'admin' || user?.customData?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
